import {getTestProps} from '@benchling/js-test-utils';
import {
  ActionIcon,
  CloseButton,
  createStyles,
  Divider,
  Group,
  Loader,
  Stack,
  Text,
  UnstyledButton,
} from '@mantine/core';
import React, {useEffect, useState} from 'react';

import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {useFileContent} from 'llm/chat-library/layouts/analysis-workspace/FileViewerBody';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import {
  AGENT_SKILL_FILE_QUERY,
  AgentSkillFile,
  AgentSkillFileQueryData,
  AgentSkillFileQueryVariables,
  SkillSummary,
} from './SkillsDAO';

interface SkillDetailPanelProps {
  skill: SkillSummary;
  onClose: () => void;
}

const useStyles = createStyles((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    padding: theme.spacing.md,
    overflowY: 'auto',
  },
  containerFileView: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    padding: theme.spacing.md,
    overflow: 'hidden',
  },
  fileViewPathGroup: {
    minWidth: 0,
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: theme.spacing.sm,
  },
  titleBlock: {
    flex: 1,
    minWidth: 0,
  },
  description: {
    whiteSpace: 'pre-wrap',
  },
  fileList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  fileItem: {
    padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
    borderRadius: theme.radius.sm,
    cursor: 'pointer',
    textAlign: 'left',
    width: '100%',
    '&:hover': {
      backgroundColor: theme.colors.gray[1],
    },
  },
  fileViewHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing.sm,
  },
  fileViewContent: {
    flex: 1,
    backgroundColor: theme.colors.gray[0],
    border: `1px solid ${theme.colors.gray[3]}`,
    borderRadius: theme.radius.sm,
    padding: theme.spacing.sm,
    fontFamily: theme.fontFamilyMonospace,
    fontSize: theme.fontSizes.xs,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all',
    overflowY: 'auto',
  },
}));

interface SkillFileViewProps {
  file: AgentSkillFile;
  onBack: () => void;
  onClose: () => void;
  classes: ReturnType<typeof useStyles>['classes'];
}

function SkillFileView({file, onBack, onClose, classes}: SkillFileViewProps): JSX.Element {
  const presignedUrlResult = useSkippableBenchlingQuery<
    AgentSkillFileQueryData,
    AgentSkillFileQueryVariables
  >(AGENT_SKILL_FILE_QUERY, {
    variables: {id: file.id},
    fetchPolicy: 'network-only',
  });

  const presignedUrl =
    presignedUrlResult.type === QueryResultType.SUCCESS
      ? (presignedUrlResult.data.agentSkillFile?.presignedUrl ?? null)
      : null;

  const isUrlLoading = presignedUrlResult.type === QueryResultType.PENDING;
  const urlError =
    presignedUrlResult.type === QueryResultType.ERROR ||
    (presignedUrlResult.type === QueryResultType.SUCCESS && presignedUrl == null);

  const {content, isLoading: isContentLoading, error: contentError} = useFileContent(presignedUrl);

  const isLoading = isUrlLoading || (presignedUrl != null && isContentLoading);
  const error = urlError ? 'Failed to load file URL.' : contentError;

  return (
    <div
      className={classes.containerFileView}
      {...getTestProps({component: 'SkillDetailPanel', element: 'fileView'})}
    >
      <div className={classes.fileViewHeader}>
        <Group spacing="xs" noWrap={true} className={classes.fileViewPathGroup}>
          <ActionIcon aria-label="Back to file list" onClick={onBack} size="sm" variant="subtle">
            <BenchlingIcon icon="chevron-left" />
          </ActionIcon>
          <Text size="xs" ff="monospace" truncate={true}>
            {isLoading ? 'Loading…' : file.virtualPath}
          </Text>
        </Group>
        <CloseButton
          onClick={onClose}
          size="sm"
          {...getTestProps({component: 'SkillDetailPanel', element: 'closeButton'})}
        />
      </div>

      <Divider my="sm" />

      {isLoading && <Loader size="xs" />}
      {error && (
        <Text size="xs" c="red">
          {error}
        </Text>
      )}
      {!isLoading && !error && (
        <div className={classes.fileViewContent}>
          {content ?? (
            <Text size="xs" c="dimmed">
              File content could not be loaded.
            </Text>
          )}
        </div>
      )}
    </div>
  );
}

export default function SkillDetailPanel({skill, onClose}: SkillDetailPanelProps): JSX.Element {
  const {classes} = useStyles();
  const [viewingFile, setViewingFile] = useState<AgentSkillFile | null>(null);

  useEffect(() => {
    setViewingFile(null);
  }, [skill.id]);

  const formattedDate = skill.createdAt ? new Date(skill.createdAt).toLocaleDateString() : null;

  if (viewingFile) {
    return (
      <SkillFileView
        file={viewingFile}
        onBack={() => {
          setViewingFile(null);
        }}
        onClose={onClose}
        classes={classes}
      />
    );
  }

  return (
    <div className={classes.container} {...getTestProps({component: 'SkillDetailPanel', element: 'root'})}>
      <div className={classes.header}>
        <div className={classes.titleBlock}>
          <Text fw={700} size="lg">
            {skill.name}
          </Text>
          {skill.creatorName && (
            <Text size="sm" c="dimmed">
              by {skill.creatorName}
            </Text>
          )}
          {formattedDate && (
            <Text size="xs" c="dimmed">
              Created {formattedDate}
            </Text>
          )}
        </div>
        <CloseButton
          onClick={onClose}
          size="sm"
          {...getTestProps({component: 'SkillDetailPanel', element: 'closeButton'})}
        />
      </div>

      <Divider my="sm" />

      <Stack spacing="xs">
        <Text size="sm" fw={600}>
          Description
        </Text>
        <Text size="sm" className={classes.description}>
          {skill.description || (
            <Text size="sm" c="dimmed" component="span">
              No description provided.
            </Text>
          )}
        </Text>
      </Stack>

      {skill.skillFiles.length > 0 && (
        <>
          <Divider my="sm" />
          <Stack spacing="xs">
            <Text size="sm" fw={600}>
              Files ({skill.skillFiles.length})
            </Text>
            <div className={classes.fileList}>
              {skill.skillFiles.map((file) => (
                <UnstyledButton
                  key={file.id}
                  className={classes.fileItem}
                  onClick={() => {
                    setViewingFile(file);
                  }}
                  {...getTestProps({component: 'SkillDetailPanel', element: 'fileItem'})}
                >
                  <Text size="xs" ff="monospace">
                    {file.virtualPath}
                  </Text>
                </UnstyledButton>
              ))}
            </div>
          </Stack>
        </>
      )}
    </div>
  );
}
